import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Printer, QrCode, Calendar } from "lucide-react";

export function fmtTicketDate(iso) {
  if (!iso) return "Date TBA";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "Date TBA";
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return "Date TBA"; }
}

export function downloadTicketPNG(t, idx, total) {
  const canvas = document.createElement("canvas");
  canvas.width = 800; canvas.height = 1200;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0a1e3d"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#1a3a5c"; ctx.fillRect(0, 0, canvas.width, 140);
  ctx.fillStyle = "#ffffff"; ctx.font = "bold 38px Arial"; ctx.textAlign = "center";
  ctx.fillText("BLUE vs. BLUE", 400, 60);
  ctx.fillStyle = "#7eb8da"; ctx.font = "20px Arial";
  ctx.fillText("Event Ticket", 400, 95);
  ctx.font = "16px Arial"; ctx.fillText(`Ticket ${idx + 1} of ${total}`, 400, 120);
  ctx.fillStyle = "#ffffff"; ctx.font = "bold 36px Arial";
  const title = t.event_title || "Event";
  ctx.fillText(title.length > 30 ? title.slice(0, 27) + "..." : title, 400, 210);
  ctx.fillStyle = "#7eb8da"; ctx.font = "20px Arial";
  ctx.fillText(t.ticket_type_name || "General Admission", 400, 250);
  ctx.fillStyle = "#cccccc"; ctx.font = "18px Arial";
  ctx.fillText(fmtTicketDate(t.event_date), 400, 285);

  const finalize = () => {
    ctx.fillStyle = "#ffffff"; ctx.font = "16px Arial";
    ctx.fillText(`Attendee: ${t.attendee_name || ""}`, 400, 880);
    ctx.fillStyle = "#7eb8da"; ctx.font = "14px monospace";
    ctx.fillText(t.qr_data || t.ticket_id, 400, 910);
    ctx.fillStyle = "#7eb8da"; ctx.font = "16px Arial";
    ctx.fillText("Present this QR at the entrance for check-in", 400, 1110);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `BvB-Ticket-${t.ticket_id || idx + 1}.png`;
    a.click();
    toast.success("Ticket downloaded!");
  };

  if (t.qr_code) {
    const qrImg = new Image();
    qrImg.onload = () => {
      ctx.fillStyle = "#ffffff"; ctx.fillRect(245, 350, 310, 500);
      ctx.drawImage(qrImg, 270, 375, 260, 260);
      ctx.fillStyle = "#0a1e3d"; ctx.font = "bold 16px Arial";
      ctx.fillText("SCAN AT ENTRY", 400, 680);
      ctx.font = "14px Arial";
      ctx.fillText(`Ticket ID: ${t.ticket_id}`, 400, 710);
      ctx.fillText(`Status: ${(t.status || "valid").toUpperCase()}`, 400, 735);
      finalize();
    };
    qrImg.onerror = () => finalize();
    qrImg.src = `data:image/png;base64,${t.qr_code}`;
  } else { finalize(); }
}

export function printTicket(t) {
  const w = window.open("", "_blank", "width=600,height=800");
  if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
  w.document.write(`<html><head><title>Blue vs Blue — Ticket</title><style>body{margin:0;font-family:Arial,sans-serif;padding:24px}.c{border:2px solid #0a1e3d;border-radius:12px;padding:24px;max-width:480px;margin:0 auto}h1{margin:0;font-size:24px;text-align:center}h2{margin:8px 0;font-size:18px}.qr{display:flex;justify-content:center;margin:16px 0}.qr img{width:240px}.info{font-size:13px;line-height:1.6}@media print{body{padding:0}}</style></head><body><div class="c"><h1>BLUE vs. BLUE</h1><div style="text-align:center;font-size:13px;margin:6px 0">Event Ticket</div><h2>${t.event_title || "Event"}</h2><div class="info"><div><b>Type:</b> ${t.ticket_type_name || "General Admission"}</div><div><b>Date:</b> ${fmtTicketDate(t.event_date)}</div><div><b>Attendee:</b> ${t.attendee_name || ""}</div></div><div class="qr"><img src="data:image/png;base64,${t.qr_code}"/></div><div style="text-align:center;font-family:monospace;font-size:11px;background:#f1f5f9;padding:4px 8px;border-radius:4px">${t.qr_data || t.ticket_id}</div><div class="info" style="text-align:center;margin-top:12px;font-size:11px;color:#64748b">Present this QR code at entry for check-in.</div></div><script>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
}

export function TicketCard({ ticket, idx, total }) {
  return (
    <div className="bg-gradient-to-br from-deep-blue to-slate-800 rounded-2xl p-6 text-white shadow-xl" data-testid={`ticket-card-${ticket.ticket_id}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs text-blue-300 uppercase tracking-widest">Ticket {idx + 1} of {total}</p>
          <h3 className="text-xl font-bold mt-1">{ticket.event_title || "Event"}</h3>
          <p className="text-sm text-blue-200 mt-1">{ticket.ticket_type_name || "General Admission"}</p>
          {ticket.event_date && <p className="text-xs text-blue-200 mt-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtTicketDate(ticket.event_date)}</p>}
        </div>
        <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">{(ticket.status || "valid").toUpperCase()}</span>
      </div>

      <div className="bg-white rounded-xl p-4 my-4">
        {ticket.qr_code
          ? <img src={`data:image/png;base64,${ticket.qr_code}`} alt="QR Code" className="w-full h-auto max-w-xs mx-auto" />
          : <div className="aspect-square flex items-center justify-center text-slate-400"><QrCode className="w-16 h-16" /></div>}
        <p className="text-center text-xs text-slate-500 mt-2 font-mono break-all">{ticket.qr_data || ticket.ticket_id}</p>
      </div>

      <div className="text-xs text-blue-200 mb-3">
        <p>Attendee: {ticket.attendee_name}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => downloadTicketPNG(ticket, idx, total)} className="flex-1 bg-white text-deep-blue hover:bg-slate-100" data-testid={`download-ticket-${ticket.ticket_id}`}>
          <Download className="w-4 h-4 mr-2" /> Download
        </Button>
        <Button onClick={() => printTicket(ticket)} variant="outline" className="flex-1 border-white/40 text-white hover:bg-white/10" data-testid={`print-ticket-${ticket.ticket_id}`}>
          <Printer className="w-4 h-4 mr-2" /> Print
        </Button>
      </div>
    </div>
  );
}
